import React, { Fragment, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useMutation, useQuery } from "react-query"
import { useParams } from "react-router-dom"

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import OutputOutlinedIcon from "@mui/icons-material/OutputOutlined"
import { Grid, IconButton, MenuItem, Skeleton } from "@mui/material"
import { Button, Menu, NotificationUtils, Tab, Tabs, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { SelectionCard, SelectionLoaderCard } from "../components/cards/SelectionCard"
import { DebouncedInput } from "../components/form/DebouncedInput"
import { KonanEmptyState } from "../components/utility/KonanEmptyState"
import { SimulationTagMap } from "../features/Simulations"
import { SimulationOutput } from "../features/simulationsReport/SimulationOutput"
import { SimulationReport } from "../features/simulationsReport/SimulationReport"
import { KonanAPI } from "../services/KonanAPI"
import { queryClient } from "../store/QueryClientStore"
import type { PaginatedSimulationJobRetrieveList } from "../types/generated/api/PaginatedSimulationJobRetrieveList"
import { Report } from "../types/generated/api/Report"
import type { SimulationJobRetrieve } from "../types/generated/api/SimulationJobRetrieve"
import { extractPageFromBackEndPaginationLink } from "../utils/genericHelpers"

import styles from "./Simulations.module.scss"

/**
 * Workflow Simulations screen
 * @returns {React.ReactElement}
 */
export function Simulations(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  const { t } = useTranslation("simulations", {
    keyPrefix: "simulationsPage",
  })

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastReportRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isSimulationsLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  // caching simulations so that when searching
  // the content doesn't go blank/change till the user selects a report from the side menu
  const cachedSimulation = useRef<SimulationJobRetrieve | undefined>(undefined)

  const [selectedCard, setSelectedCard] = useState<{ uuid: string; name: string } | null>(null)
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false)
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null)

  const [tab, setTab] = useState<"dashboard" | "output">("dashboard")

  const {
    isLoading: isSimulationsLoading,
    isFetchingNextPage,
    data: simulationsData,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedSimulationJobRetrieveList>, AxiosError, SimulationJobRetrieve>(
    ["SimulationReports", projectId, searchValue],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchSimulations({
        projectId: projectId as string,
        pageSize: 20,
        page: pageParam,
        ...(!!searchValue && { workflow_name: searchValue }),
      }),
    {
      select: (data) => ({
        pageParams: data.pageParams,
        pages:
          data?.pages
            .reduce((accumulator, page) => {
              return accumulator.concat(page.data.results as SimulationJobRetrieve[])
            }, [] as SimulationJobRetrieve[])
            .map((item: SimulationJobRetrieve): SimulationJobRetrieve => {
              return {
                ...item,
                status: item.status === "Sent" ? "Success" : item.status,
              }
            }) ?? [],
      }),
      onSuccess: ({ pages }) => {
        if (!selectedCard) {
          setSelectedCard({ uuid: pages[0]?.uuid ?? "", name: pages[0]?.workflow_name as string })
          cachedSimulation.current = {
            ...(pages[0] as SimulationJobRetrieve),
            status: (pages[0]?.status as string) === "Sent" ? "Success" : (pages[0]?.status as string),
          }
        }
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId,
      refetchOnMount: true,
    },
  )

  const selectedSimulation = useMemo(
    () =>
      simulationsData?.pages.find((simulation) => simulation.uuid === selectedCard?.uuid) ?? cachedSimulation.current,
    [selectedCard?.uuid, simulationsData?.pages],
  )

  const { data: simulationReportRes, isFetching: isSimulationReportLoading } = useQuery<
    AxiosResponse<Report>,
    AxiosError
  >(
    ["simulation_report", selectedSimulation?.uuid, projectId, selectedSimulation?.report],
    () =>
      KonanAPI.fetchInsightsReport({
        project_uuid: projectId as string,
        report_uuid: selectedSimulation?.report as string,
      }),
    { enabled: !!projectId && !!selectedSimulation?.uuid && !!selectedSimulation?.report },
  )

  const cancelSimulationMutation = useMutation<
    AxiosResponse,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    { projectId: string; simulation_uuid: string }
  >(KonanAPI.cancelSimulation, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["SimulationReports", projectId])

      NotificationUtils.toast(t("notifications.cancelReport.success"), { severity: "success" })
    },
    onError: async (error) => {
      const errorCode = error.response?.data?.errors?.[0]?.code

      if (errorCode === "SIM_100") {
        await queryClient.invalidateQueries(["SimulationReports", projectId])

        await cancelSimulation()
      } else {
        NotificationUtils.toast(t("notifications.cancelReport.error"), { severity: "error" })
      }
    },
  })

  const cancelReportMutation = useMutation<AxiosResponse, AxiosError, { project_uuid: string; report_uuid: string }>(
    KonanAPI.cancelInsightsReport,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["SimulationReports", projectId])

        NotificationUtils.toast(t("notifications.cancelReport.success"), { severity: "success" })
      },
      onError: () => {
        NotificationUtils.toast(t("notifications.cancelReport.error"), { severity: "error" })
      },
    },
  )

  const cancelSimulation = async (): Promise<void> => {
    if (selectedSimulation?.report) {
      await cancelReportMutation.mutateAsync({
        project_uuid: projectId as string,
        report_uuid: selectedSimulation?.report as string,
      })
    } else {
      await cancelSimulationMutation.mutateAsync({
        projectId: projectId as string,
        simulation_uuid: selectedSimulation?.uuid as string,
      })
    }
  }

  // to prevent table from using cached data while the report is loading
  useEffect(() => {
    return () => {
      queryClient.resetQueries({ queryKey: ["SimulationReports", projectId] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Grid container item xs={12} display={"flex"} flex={1} wrap="nowrap" className={styles.root}>
      {isSimulationsLoading || (!isSimulationsLoading && (!!simulationsData?.pages.length || !!searchValue)) ? (
        // TODO:: refactor into an HoC to make it easily reusable
        <Fragment>
          <Grid container item direction={"column"} flexWrap={"nowrap"} xs className={styles.simulationsContainer}>
            {/* header */}
            <Grid item className={styles.header}>
              {isSimulationsLoading && !searchValue ? (
                <Skeleton animation="wave" width="200px" />
              ) : (
                <Tabs value={tab}>
                  <Tab
                    value={"dashboard"}
                    label={t("tabs.dashboard")}
                    icon={() => <DashboardOutlinedIcon />}
                    selected={tab === "dashboard"}
                    onClick={() => setTab("dashboard")}
                  />

                  <Tab
                    value={"output"}
                    label={t("tabs.output")}
                    icon={() => <OutputOutlinedIcon />}
                    selected={tab === "output"}
                    onClick={() => setTab("output")}
                  />
                </Tabs>
              )}

              <Grid item display={"flex"} gap={1}>
                {!isSidebarOpen && (
                  <Button
                    type="button"
                    variant="secondary"
                    startIcon={<AssignmentOutlinedIcon style={{ width: "20px", height: "20px" }} />}
                    onClick={() => {
                      setSearchValue(null)
                      setIsSidebarOpen((prev) => !prev)
                    }}
                  >
                    {t("simulationReports")}
                  </Button>
                )}
                {!isSidebarOpen &&
                  (["Running", "Pending"].includes(selectedSimulation?.status as string) ||
                    [Report.status.PENDING, Report.status.PREPARATION, Report.status.RUNNING].includes(
                      simulationReportRes?.data?.status as Report.status,
                    )) && (
                    <Grid item display={"flex"}>
                      <IconButton
                        style={{ padding: "0px" }}
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setMoreMenuAnchor(event.currentTarget)}
                        aria-label="settings"
                        size="small"
                      >
                        <MoreVertIcon sx={{ color: "var(--base-text-2)" }} />
                      </IconButton>

                      <Menu
                        anchorEl={moreMenuAnchor}
                        open={Boolean(moreMenuAnchor)}
                        onClose={() => setMoreMenuAnchor(null)}
                        menuMaxContent
                      >
                        <MenuItem
                          disabled={cancelSimulationMutation.isLoading || cancelReportMutation.isLoading}
                          onClick={() => {
                            cancelSimulation()

                            setMoreMenuAnchor(null)
                          }}
                          key={"delete-cancel-btn"}
                        >
                          <Typography variant="label-bold" color="negative" variantColor={2}>
                            {t("menuActions.cancelReport")}
                          </Typography>
                        </MenuItem>
                      </Menu>
                    </Grid>
                  )}
              </Grid>
            </Grid>

            {/* Body */}
            {selectedSimulation?.status === "Failed" && !selectedSimulation?.report ? (
              <Grid item xs={12} alignContent={"center"}>
                <KonanEmptyState title={t("emptyState.failed.title")} subTitle={t("emptyState.failed.subtitle")} />
              </Grid>
            ) : (
              <Grid item xs={12} p={2} className={styles.content_container} alignContent={"initial"}>
                {tab === "dashboard" && (
                  <SimulationReport
                    simulationJob={selectedSimulation}
                    reportData={simulationReportRes?.data}
                    isContainerLoading={(isSimulationsLoading || isSimulationReportLoading) && !searchValue}
                    cancelSimulation={cancelSimulation}
                    isCancelMutationLoading={cancelSimulationMutation.isLoading || cancelReportMutation.isLoading}
                  />
                )}
                {tab === "output" && <SimulationOutput simulationId={selectedSimulation?.uuid} />}
              </Grid>
            )}
          </Grid>

          {/* Side-bar */}
          {isSidebarOpen && (
            <Grid item className={styles.sideBar}>
              <Grid container item xs={12} p={2} pb={0}>
                <Grid item xs={12} display={"flex"} justifyContent={"space-between"} alignItems={"center"} mb={1}>
                  <Typography variant="label-bold" textTransform="initial">
                    {t("simulationReports")}
                  </Typography>
                  <Button
                    variant="ghost-secondary"
                    size="small"
                    type="button"
                    onClick={() => {
                      setSearchValue(null)
                      setIsSidebarOpen((prev) => !prev)
                    }}
                  >
                    {<CloseOutlinedIcon style={{ width: "16px", height: "16px" }} />}
                  </Button>
                </Grid>

                <Grid item xs={12} mb={2}>
                  <DebouncedInput
                    callback={(value: string | null) => {
                      setSearchValue(value)
                    }}
                    fullWidth
                    variant={"filled"}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12} className={styles.sideBar_content} id="simulation-scrollable-target">
                {isSimulationsLoading ? (
                  <Grid container item xs={12} gap={1.5} pl={2} pr={1.5} pb={2}>
                    {[1, 2, 3].map((item) => (
                      <Grid item xs={12} key={item}>
                        <SelectionLoaderCard />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Grid container item xs={12} gap={1.5} pl={2} pr={1.5} pb={2}>
                    {!!simulationsData?.pages.length ? (
                      <Fragment>
                        {simulationsData.pages.map((simulationJob: SimulationJobRetrieve, index: number) => (
                          <Grid
                            item
                            xs={12}
                            key={simulationJob.uuid}
                            ref={hasNextPage && index === simulationsData.pages.length - 1 ? lastReportRef : undefined}
                          >
                            <SelectionCard
                              item={{
                                createdAt: simulationJob.created_at,
                                title: simulationJob.workflow_name,
                                state: t(`status.${simulationJob.status}`, {
                                  defaultValue: simulationJob.status,
                                }),
                                version: simulationJob.version,
                              }}
                              state={
                                simulationJob.uuid === selectedCard?.uuid
                                  ? "selected"
                                  : ["Running", "Pending"].includes(simulationJob.status)
                                    ? "pending"
                                    : "default"
                              }
                              onClick={() => {
                                setSelectedCard({ uuid: simulationJob.uuid, name: simulationJob.workflow_name })
                                cachedSimulation.current = simulationJob
                                // to prevent table from using cached data while in fact it doesn't belong to the report being fetched
                                queryClient.resetQueries({ queryKey: ["simulation-outputs", projectId] })
                              }}
                              tagVariant={SimulationTagMap[simulationJob.status as keyof typeof SimulationTagMap]}
                            />
                          </Grid>
                        ))}
                        {hasNextPage && (
                          <Grid container item xs={12} gap={1.5}>
                            {[1, 2, 3].map((item) => (
                              <Grid item xs={12} key={item}>
                                <SelectionLoaderCard />
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Fragment>
                    ) : (
                      <KonanEmptyState
                        title={t("emptyState.noResults.title", { value: searchValue })}
                        subTitle={t("emptyState.noResults.subtitle")}
                      />
                    )}
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </Fragment>
      ) : (
        <KonanEmptyState title={t("emptyState.noReports.title")} subTitle={t("emptyState.noReports.subtitle")} />
      )}
    </Grid>
  )
}
